import type {
  BusinessBrandFilter,
  BusinessCockpitResponse,
  BusinessSkuEconomicsResponse,
  BusinessWindowPreset,
} from '@nadoutong/contracts';
import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { useSession } from '../hooks/useSession.tsx';
import { opsApiRequest } from './client.ts';

// The projection page kinds ('overview'/'customers'/'supply'/'risk') and
// the 'revenue' kind were retired with their pages (#1003 revenue, #1007
// the rest); the only live business page fetches are the morning cockpit
// (#1004) and SKU unit economics (#1006, now the metrics hub's SKU tab
// after #1643 S5). The hub's pnl tab (its own /v1/ops/finance/pnl
// source) and the risk workbench (/v1/ops/risks) do not go through this
// fetcher.
export type BusinessFetchPageKind = 'cockpit' | 'sku-economics';

export interface BusinessQuery {
  readonly window: BusinessWindowPreset;
  readonly brand: BusinessBrandFilter;
  readonly from?: string;
  readonly to?: string;
}

type BusinessPageResponseMap = {
  readonly 'sku-economics': BusinessSkuEconomicsResponse;
  readonly cockpit: BusinessCockpitResponse;
};

const BASE = '/v1/ops/business';

export const BUSINESS_QUERY_KEYS = {
  page: (page: BusinessFetchPageKind, query: BusinessQuery) => [
    'business',
    page,
    query.window,
    query.brand,
    query.from ?? '',
    query.to ?? '',
  ] as const,
};

export async function getBusinessPage<TPage extends BusinessFetchPageKind>(
  page: TPage,
  query: BusinessQuery,
  fetchImpl: typeof fetch,
  signal?: AbortSignal,
): Promise<BusinessPageResponseMap[TPage]> {
  return getBusinessPageByPath<BusinessPageResponseMap[TPage]>(page, query, fetchImpl, signal);
}

async function getBusinessPageByPath<TResponse>(
  page: BusinessFetchPageKind,
  query: BusinessQuery,
  fetchImpl: typeof fetch,
  signal?: AbortSignal,
): Promise<TResponse> {
  const qs = new URLSearchParams({
    window: query.window,
    brand: query.brand,
  });
  if (query.window === 'custom') {
    if (query.from !== undefined && query.from.length > 0) qs.set('from', query.from);
    if (query.to !== undefined && query.to.length > 0) qs.set('to', query.to);
  }
  return opsApiRequest<TResponse>(
    `${BASE}/${page}?${qs.toString()}`,
    { signal },
    fetchImpl,
  );
}

export function useBusinessPage<TPage extends BusinessFetchPageKind>(
  page: TPage,
  query: BusinessQuery,
): UseQueryResult<BusinessPageResponseMap[TPage]> {
  return useBusinessEndpoint<BusinessPageResponseMap[TPage]>(page, query);
}

function useBusinessEndpoint<TResponse>(
  page: BusinessFetchPageKind,
  query: BusinessQuery,
): UseQueryResult<TResponse> {
  const { fetchImpl } = useSession();
  return useQuery<TResponse, Error>({
    queryKey: BUSINESS_QUERY_KEYS.page(page, query),
    queryFn: ({ signal }) => getBusinessPageByPath<TResponse>(page, query, fetchImpl, signal),
  });
}
